import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { format } from "date-fns";
import { t } from "i18next";
import { useMemo } from "react";

import { ErrorMessages } from "@pdpp/lib-planta";
import { ResponseNotOkError } from "@pdpp/lib-react";
import type { WebApi } from "@pdpp/planta-server";

import { plantaApi } from "../../api/fetch";
import { ApiErrors, parseApiErrors } from "../../utils";
import { Weekdays, weekdays } from "../../utils/weekdays";
import { ORG_NODE_BY_ID, orgNodes$ } from "../org/state";
import type { DepartmentDayTemplates, Template } from "../overview/types";
import type { DatePeriod } from "../period";
import { clearHeadcountCache } from "./api";
import { attachDepartmentTemplates } from "./transforms";

/** Shared across overview/plan mounts so remounts do not refetch immediately. */
export const PLAN_QUERY_STALE_TIME_MS = 5 * 60 * 1000;

export const departmentDayTemplatesQueryKey = (
  orgNodeId: string,
): readonly ["department-day-templates", string] => [
  "department-day-templates",
  orgNodeId,
];

//NOTE: One approach on how to reuse types from backend,
export type DayTemplate = WebApi["postDayTemplate"]["input"]["body"];
export type DayTemplateDatesInput = WebApi["postDayTemplateDates"]["input"];

export interface PlanMethodAssigned {
  readonly name: string;
  readonly dates: ReadonlyArray<string>;
  readonly id: number;
}

export interface PlanMethod {
  readonly id: string;
  readonly name: string;
  readonly orgActivityIds: Array<number>;
  readonly validPeriod: DatePeriod;
  readonly templates: Template[];
  readonly dates: PlanMethodAssigned[];
  readonly workloadDriverName: string;
  readonly workloadDriverLabel: string;
  readonly workloadDriverSubType: string;
}

export interface PlanMethodFromApi extends Omit<PlanMethod, "validPeriod"> {
  readonly validPeriod: {
    start: string;
    end: string;
  };
}

interface UseGetMethodsProps {
  readonly orgNodeId: string;
}

interface UseGetMethods {
  readonly data: ReadonlyArray<PlanMethod>;
  readonly isLoading: boolean;
  readonly refetch: () => void;
}

export const isDepartmentOrgNode = (orgNodeId: string): boolean =>
  ORG_NODE_BY_ID.get(orgNodeId)?.type === "department";

export async function fetchDepartmentDayTemplates(
  departmentId: string,
  signal?: AbortSignal,
): Promise<DepartmentDayTemplates> {
  return plantaApi.jsonInJsonOut<DepartmentDayTemplates>(
    `/plan/dayTemplates?departmentId=${encodeURIComponent(departmentId)}`,
    { method: "GET", signal },
  );
}

export const useDepartmentDayTemplates = (
  orgNodeId: string,
): UseQueryResult<DepartmentDayTemplates> => {
  const orgNodesLength = orgNodes$.value.length;
  const isEnabled = orgNodesLength > 0 && isDepartmentOrgNode(orgNodeId);

  return useQuery({
    queryKey: departmentDayTemplatesQueryKey(orgNodeId),
    queryFn: ({ signal }) => fetchDepartmentDayTemplates(orgNodeId, signal),
    enabled: isEnabled,
    staleTime: PLAN_QUERY_STALE_TIME_MS,
  });
};

export const useGetMethods = ({
  orgNodeId,
}: UseGetMethodsProps): UseGetMethods => {
  const orgNodesLength = orgNodes$.value.length;
  const isEnabled = orgNodesLength > 0 && isDepartmentOrgNode(orgNodeId);
  const planMethodsQueryKey = ["department-plan-methods", orgNodeId] as const;

  const {
    data: departmentTemplates,
    isPending: isTemplatesPending,
    refetch: refetchTemplates,
  } = useDepartmentDayTemplates(orgNodeId);

  const {
    isPending: isMethodsLoading,
    data: methods,
    refetch: refetchMethods,
  } = useQuery({
    queryKey: planMethodsQueryKey,
    queryFn: async ({ signal }) =>
      plantaApi.jsonInJsonOut<PlanMethodFromApi[]>(
        `/plan/methods/?orgId=${orgNodeId}&orgType=department`,
        { method: "GET", signal },
      ),
    enabled: isEnabled,
    staleTime: PLAN_QUERY_STALE_TIME_MS,
  });

  const isLoading = isEnabled && (isMethodsLoading || isTemplatesPending);

  // Wait for templates load; on error still show methods (cached templates or []).
  const data = useMemo(() => {
    if (!isEnabled || methods === undefined || isTemplatesPending) {
      return [];
    }
    return attachDepartmentTemplates(methods, departmentTemplates ?? []).map(
      (method) => ({
        ...method,
        validPeriod: [
          new Date(method.validPeriod.start),
          new Date(method.validPeriod.end),
        ] as DatePeriod,
      }),
    );
  }, [isEnabled, methods, departmentTemplates, isTemplatesPending]);

  return {
    data,
    isLoading,
    refetch: () => {
      void refetchMethods();
      void refetchTemplates();
    },
  };
};

export const useGetMethod = ({
  id,
}: {
  id: string;
}): {
  data: PlanMethod;
  refetch: () => void;
  dayTemplatesRefetch: () => void;
} => {
  const { data: method, refetch } = useQuery<PlanMethodFromApi>({
    initialData: {
      id: "",
      name: "",
      orgActivityIds: [],
      validPeriod: { start: new Date().toString(), end: new Date().toString() },
      templates: [],
      dates: [],
      workloadDriverName: "",
      workloadDriverLabel: "",
      workloadDriverSubType: "",
    },
    queryKey: [`plan-method-${id}`],
    queryFn: async ({ signal }) => {
      const res = await plantaApi.jsonInJsonOut<PlanMethodFromApi>(
        `/plan/methods/${id}`,
        { signal },
      );
      return res;
    },
  });

  const { data: dates } = useQuery<PlanMethodAssigned[]>({
    initialData: [],
    queryKey: [`plan-method-dates-${id}`],
    queryFn: async ({ signal }) => {
      const res = await plantaApi.jsonInJsonOut<PlanMethodAssigned[]>(
        `/plan/dayTemplateMethods/${id}/dates`,
        { signal },
      );
      return res;
    },
  });

  const { data: dayTemplates, refetch: dayTemplatesRefetch } = useQuery<
    Template[]
  >({
    queryKey: [`plan-day-template-${id}`],
    queryFn: async ({ signal }) => {
      const res = await plantaApi.jsonInJsonOut<Template[]>(
        `/plan/dayTemplateMethods/${id}/dayTemplates`,
        { signal },
      );
      return res;
    },
  });

  const templates = useMemo<Template[]>((): Template[] => {
    return (dayTemplates as Template[])?.map((template: Template) => {
      const tCopy = JSON.parse(JSON.stringify(template));
      if (
        tCopy.conditions !== undefined &&
        tCopy.conditions.weekdays !== undefined
      ) {
        tCopy.conditions.weekdays = tCopy.conditions.weekdays.map(
          (x: number) => weekdays[x - 1],
        ) as Weekdays;
      }
      return tCopy;
    });
  }, [dayTemplates]);

  return {
    data: {
      ...method,
      templates: templates ?? [],
      dates,
      validPeriod: [
        new Date(method.validPeriod.start ?? ""),
        new Date(method.validPeriod.end ?? ""),
      ] as DatePeriod,
    },
    refetch,
    dayTemplatesRefetch,
  };
};

interface PutPlanMethodArgs
  extends Omit<
    PlanMethod,
    "id" | "templates" | "dates" | "workloadDriverLabel"
  > {
  id?: string | undefined;
}

interface PutPlanMethodReturn {
  readonly errors: ReadonlyArray<string>;
  readonly saved?: boolean;
}
export async function putPlanMethod(
  values: PutPlanMethodArgs,
  departmentId: number,
): Promise<PutPlanMethodReturn | void> {
  const body = {
    items: [
      {
        base: {
          id: values.id,
          name: values.name,
          type: "DayTemplate",
          departmentId: departmentId,
          orgActivityIds: values.orgActivityIds,
          validPeriod: {
            start: format(values.validPeriod[0], "yyyy-MM-dd"),
            end: format(values.validPeriod[1], "yyyy-MM-dd"),
          },
          workloadDriverName: values.workloadDriverName || null,
          workloadDriverSubType: values.workloadDriverSubType || null,
        },
        //NOTE: In future when other planning methods than DayTemplate, this should be used.
        //variant: {}
      },
    ],
  };

  try {
    await plantaApi.jsonInVoidOut(`/plan/methods`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
    clearHeadcountCache();
  } catch (error) {
    if (error instanceof ResponseNotOkError) {
      const errors = (await error.response.json()) as ApiErrors;

      const saved = errors.errors.some(
        ({ code }) => code === ErrorMessages.methodSavedEvaluationFailed,
      );
      if (saved) {
        clearHeadcountCache();
      }
      return { errors: parseApiErrors(errors.errors, t), saved };
    }

    return { errors: [t("notifications.somethingWentWrong")] };
  }
}

interface DeletePlanMethodArgs {
  readonly id: string;
}

export async function deletePlanMethod({
  id,
}: DeletePlanMethodArgs): Promise<void> {
  return await plantaApi.jsonInVoidOut(`/plan/methods/${id}`, {
    method: "DELETE",
  });
}

export async function assignDays(
  params: DayTemplateDatesInput["params"],
  body: DayTemplateDatesInput["body"],
): Promise<void> {
  await plantaApi.jsonInVoidOut(`/plan/dayTemplates/${params.id}/dates`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  clearHeadcountCache();
}

function toCreateDayTemplateBody(values: DayTemplate): DayTemplate {
  return {
    name: values.name,
    methodId: values.methodId,
    headcounts: values.headcounts.map((headcount) => ({
      orgActivityId: headcount.orgActivityId,
      value: [...headcount.value],
      ...(headcount.effectiveStartDate != null
        ? { effectiveStartDate: headcount.effectiveStartDate }
        : {}),
      ...(headcount.effectiveEndDate != null
        ? { effectiveEndDate: headcount.effectiveEndDate }
        : {}),
    })),
    ...(values.conditions != null ? { conditions: values.conditions } : {}),
  };
}

export async function createTemplate(values: DayTemplate): Promise<void> {
  await plantaApi.jsonInVoidOut(`/plan/dayTemplates`, {
    method: "POST",
    body: JSON.stringify(toCreateDayTemplateBody(values)),
  });
  clearHeadcountCache();
}

export async function editTemplate(values: Template): Promise<void> {
  await plantaApi.jsonInVoidOut(`/plan/dayTemplates/${values.id}`, {
    method: "PUT",
    body: JSON.stringify(values),
  });
  clearHeadcountCache();
}

export async function deleteTemplate(id: number): Promise<void> {
  return await plantaApi.jsonInVoidOut(`/plan/dayTemplates/${id}`, {
    method: "DELETE",
  });
}
